import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null
    return null
}

let isRefreshing = false
let failedQueue: Array<{
    resolve: (value?: any) => void
    reject: (reason?: any) => void
}> = []

const processQueue = (error: any = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve()
        }
    })

    failedQueue = []
}

// Setup global axios interceptor
export const setupApiInterceptor = () => {
    // Request interceptor to add token from cookies
    axios.interceptors.request.use(
        async config => {
            // Always get fresh token from cookie
            const token = getCookie('stytch_session_jwt')
            if (token) {
                config.headers.Authorization = `Bearer ${token}`
            }
            return config
        },
        error => Promise.reject(error),
    )

    // Response interceptor to handle 401 and refresh token
    axios.interceptors.response.use(
        response => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

            // Check if error is 401 Unauthorized and we haven't already tried to refresh
            if (error.response?.status === 401 && !originalRequest._retry) {
                // Only trigger if not already on login page
                if (!window.location.pathname.includes('/login')) {
                    if (isRefreshing) {
                        // If already refreshing, queue this request
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject })
                        })
                            .then(() => {
                                return axios(originalRequest)
                            })
                            .catch(err => {
                                return Promise.reject(err)
                            })
                    }

                    originalRequest._retry = true
                    isRefreshing = true

                    try {
                        // Request token refresh via custom event
                        const refreshPromise = new Promise((resolve, reject) => {
                            const handleRefreshResult = (event: Event) => {
                                const customEvent = event as CustomEvent
                                window.removeEventListener('auth:refresh-result', handleRefreshResult)
                                if (customEvent.detail.success) {
                                    resolve(customEvent.detail)
                                } else {
                                    reject(customEvent.detail.error)
                                }
                            }
                            window.addEventListener('auth:refresh-result', handleRefreshResult)
                            window.dispatchEvent(new CustomEvent('auth:refresh-requested'))
                        })

                        await refreshPromise
                        processQueue()
                        isRefreshing = false

                        // Remove old Authorization header - request interceptor will add fresh one
                        if (originalRequest.headers) {
                            delete originalRequest.headers.Authorization
                        }

                        // Retry the original request
                        return axios(originalRequest)
                    } catch (refreshError) {
                        processQueue(refreshError)
                        isRefreshing = false
                        // Dispatch logout event if refresh failed
                        window.dispatchEvent(new CustomEvent('auth:unauthorized'))
                        return Promise.reject(refreshError)
                    }
                }
            }

            return Promise.reject(error)
        },
    )
}
